import { useState, useRef, useEffect } from 'react';

export type SortOption = 'newest' | 'popular' | 'pages-asc' | 'pages-desc';

interface SortDropdownProps {
  value: SortOption;
  onChange: (value: SortOption) => void;
}

const sortOptions: { value: SortOption; label: string; icon: string }[] = [
  { value: 'newest', label: 'Newest First', icon: 'ri-time-line' },
  { value: 'popular', label: 'Most Popular', icon: 'ri-fire-line' },
  { value: 'pages-asc', label: 'Pages: Low to High', icon: 'ri-sort-asc' },
  { value: 'pages-desc', label: 'Pages: High to Low', icon: 'ri-sort-desc' },
];

export default function SortDropdown({ value, onChange }: SortDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = sortOptions.find((o) => o.value === value) ?? sortOptions[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:border-emerald-400 hover:text-emerald-700 transition-all shadow-sm whitespace-nowrap cursor-pointer"
      >
        <i className={`${current.icon} text-base`}></i>
        <span>Sort: {current.label}</span>
        <i className={`ri-arrow-down-s-line text-base transition-transform ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {sortOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${
                value === opt.value
                  ? 'bg-emerald-50 text-emerald-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={`${opt.icon} text-base`}></i>
              {opt.label}
              {value === opt.value && (
                <i className="ri-check-line ml-auto text-emerald-600"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
